/**
 * useIdentitySearch — the ONE client-side hook every compose surface uses to resolve recipients
 * via the canonical Identity Service. Debounced, out-of-order-safe, and treats a fully-typed email
 * as "complete" (no dropdown), so hand-typing an address never fights the picker.
 *
 * The Inbox email composer (ComposeModal) consumes THIS — no screen re-implements resolution or
 * filters a client-side contact list. `searchStartRef` is exposed so a caller can measure
 * time-to-selection for telemetry. (Internal Communication uses NewThreadModal + the Users list,
 * not email identity resolution.)
 */
import { useEffect, useRef, useState } from 'react';
import { searchIdentities, type IdentityMatch } from './identityApi';

interface UseIdentitySearch {
  results: IdentityMatch[];
  loading: boolean;
  /** True once a query has been issued and settled with zero matches (for a no-match affordance). */
  emptyResolved: boolean;
  /** Epoch ms when the in-flight query started — null when idle. For time-to-selection telemetry. */
  searchStartRef: React.MutableRefObject<number | null>;
  /** Clear results immediately (e.g. after a pick). */
  reset: () => void;
}

/** A fully-typed email ("a@b.co") is complete → suppress the dropdown. */
function looksLikeCompleteEmail(q: string): boolean {
  return /@[^@\s]+\.[^@\s]+$/.test(q);
}

export function useIdentitySearch(rawQuery: string, minChars = 2): UseIdentitySearch {
  const [results, setResults] = useState<IdentityMatch[]>([]);
  const [loading, setLoading] = useState(false);
  const [emptyResolved, setEmptyResolved] = useState(false);
  const searchStartRef = useRef<number | null>(null);
  const lastQueryRef = useRef<string>('');

  const q = rawQuery.trim();

  useEffect(() => {
    if (q.length < minChars || looksLikeCompleteEmail(q)) {
      setResults([]); setLoading(false); setEmptyResolved(false);
      lastQueryRef.current = '';
      searchStartRef.current = null;
      return;
    }
    setLoading(true);
    setEmptyResolved(false);
    searchStartRef.current = Date.now();
    lastQueryRef.current = q;
    const t = setTimeout(async () => {
      try {
        const matches = await searchIdentities(q);
        if (lastQueryRef.current !== q) return; // a newer query superseded this one
        setResults(matches);
        setEmptyResolved(matches.length === 0);
      } catch {
        if (lastQueryRef.current === q) { setResults([]); setEmptyResolved(false); }
      } finally {
        if (lastQueryRef.current === q) setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q, minChars]);

  const reset = () => { setResults([]); setEmptyResolved(false); lastQueryRef.current = ''; };

  return { results, loading, emptyResolved, searchStartRef, reset };
}
